<script setup lang="ts">
import { CalculatorService } from '@/api/calculator/calculatorService';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MarketingConsent from '@/components/MarketingConsent/MarketingConsent.vue';
import { useUserStore } from '@/stores/userStore/userStore';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const url = ref('');
const isLoading = ref(true);
const hasError = ref(false);
const isComingSoon = ref(false);
const showMarketingConsent = ref(false);

const MARKETING_CONSENT_KEY = 'marketing_consent';

async function getUrl() {
  try {
    const response = await CalculatorService.getUniversalLink({ name: (route.query['name'] as string) ?? '' });

    if (response === '') {
      isComingSoon.value = true;
    } else {
      url.value = response;
      if (route.query['name'] === 'Planer Ślubny') {
        const marketingConsent = localStorage.getItem(MARKETING_CONSENT_KEY);
        const userMarketingConsent = userStore.getLoggedInUserMarketingConsent;
        console.log(userMarketingConsent);

        if ((marketingConsent === null || marketingConsent === 'false') && !userMarketingConsent) {
          showMarketingConsent.value = true;
        }
      }
    }
    hasError.value = false;
  } catch (_error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function refreshPage() {
  window.location.reload();
}

function goBack() {
  router.back();
}

onMounted(() => {
  getUrl();
});
</script>

<template>
  <div class="calculator">
    <MarketingConsent v-if="showMarketingConsent" />
    <button
      class="calculator__back-button"
      @click="goBack"
    >
      <i class="pi pi-arrow-left"></i>
      <span>Powrót</span>
    </button>
    <div
      v-if="isLoading"
      class="calculator__spinner-wrapper"
    >
      <div class="calculator__spinner"></div>
    </div>
    <div
      v-else-if="hasError"
      class="calculator__error-wrapper"
    >
      <p class="calculator__error-message">Wystąpił błąd podczas ładowania kalkulatora.</p>
      <button
        class="calculator__refresh-button"
        @click="refreshPage"
      >
        Odśwież stronę
      </button>
    </div>
    <div
      v-else-if="isComingSoon"
      class="calculator__coming-soon-wrapper"
    >
      <p class="calculator__coming-soon-message">Kalkulator będzie dostępny wkrótce.</p>
    </div>
    <iframe
      v-else
      class="calculator-frame"
      :src="url"
    ></iframe>
  </div>
</template>

<style lang="scss" scoped>
.calculator {
  position: fixed;
  top: 60px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  margin: 0;
  padding: 0;
  border: none;

  &__back-button {
    position: fixed;
    top: 90px;
    left: 40px;
    z-index: 1000;
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background-color: $brown-200;
    color: white;
    border: none;
    border-radius: 0.8rem;
    font-size: 1.6rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: $media-max-tablet) {
      top: 70px;
      left: 20px;
      padding: 0.8rem 1.6rem;
      font-size: 1.4rem;
    }

    &:hover {
      background-color: rgba($brown-200, 0.9);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba($brown-200, 0.3);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba($brown-200, 0.2);
    }

    i {
      font-size: 1.5rem;

      @media (max-width: $media-max-tablet) {
        font-size: 1.3rem;
      }
    }

    span {
      font-size: 1.6rem;
      font-weight: 600;

      @media (max-width: $media-max-tablet) {
        font-size: 1.4rem;
      }
    }
  }

  &-frame {
    width: 100%;
    height: 100%;
    border: none;
  }

  &__spinner-wrapper,
  &__error-wrapper,
  &__coming-soon-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    gap: 2rem;
  }

  &__spinner {
    border: 6px solid $brown-600;
    border-top: 6px solid $brown-200;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    animation: spin 1s linear infinite;
  }

  &__error-message,
  &__coming-soon-message {
    font-size: 4rem;
    color: $brown-200;
    text-align: center;
    font-weight: 600;
  }

  &__refresh-button {
    padding: 1rem 2rem;
    background-color: $brown-200;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 2.5rem;
    transition: background-color 0.2s;

    &:hover {
      background-color: $brown-700;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
